import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { Box, Copy, Icon, IconWrapper, Image, Optional } from '@honda-canada/design-system-react';

import DealerStatusAndHours from '../shared/DealerStatusAndHours';
import HeaderContext from '../service/HeaderContext';
import Link from '../../RoutableSitecoreLink';
import MegaMenuGroups from './MegaMenuGroups';
import MegaMenuSimple from './MegaMenuSimple';
import themeStyles from '../styles/Header.styles';
import useDealerStatusAndHours from '../service/useDealerStatusAndHours';
import { ALIGNMENT_RIGHT, FIND_DEALER_NAV_ITEM_ID } from '../service/constants';
import { PropItemsType } from '../../../utils/propTypes';
import { getGtmTagValue, getGtmTitleValue, pushDealerGtmEvent, pushGlobalNavGtmEvent } from '../../../utils/gtmEvents';
import useHideDealerByProvinces from '../service/useHideDealerByProvinces';

const NavList = themeStyles.apply(Box, 'NavList');
const NavItem = themeStyles.apply(Link, 'NavItem');
const NavItemWrapper = themeStyles.apply(Box, 'NavItemWrapper');
const NavItemIcon = themeStyles.apply(Image, 'NavItemIcon');
const NavItemIconWrapper = themeStyles.apply(IconWrapper, 'NavIconWrapper');
const NavItemLabel = themeStyles.apply(Copy, 'NavItemLabel');

const MainMenuItems = ({ renderItems }) => {
  const {
    handleAction,
    activeMenu,
    firstOpen,
    gtmTags,
    megaMenuRef,
    navItemLabelUnderline,
    navItemUnderline,
    primaryItemsRef,
    setActiveMenu,
    setSelectedMenu,
    showMegaMenuGroups,
    showMegaMenuSimple,
    showNavItemIcon,
    styles,
    isFindDealerIconLeftSide,
    shouldNavLabelHoverGrey,
    shouldUnderlineLinkNavItems,
  } = useContext(HeaderContext);

  const { isDealerDataAvailable, dealerName } = useDealerStatusAndHours();
  const { hideDealer } = useHideDealerByProvinces();

  const handleEvent = ({ itemName, isLink, event, linkUrl, linkText }) => {
    const isDealerLocatorLink = itemName === 'FindDealer';
    handleAction(activeMenu === itemName ? null : itemName, isLink, event);
    if (isDealerLocatorLink) {
      setActiveMenu(null);
      setSelectedMenu(itemName);
      pushDealerGtmEvent({ dealerName, linkText: 'find a dealer' });
    } else if (isLink) {
      setActiveMenu(null);
      setSelectedMenu(itemName);
      pushGlobalNavGtmEvent(linkText, linkUrl);
    }
  };

  const renderMenuItems = renderItems?.map((itemParams = {}) => {
    const { url, itemId, itemName, label, icon, ctaLink, gtmTitle, gtmInteractionType, items, navAlignment } =
      itemParams;
    const hasItems = !!items?.length;
    const link = (url?.href && url) || (ctaLink?.href && ctaLink);
    const linkUrl = link?.href;
    const isLink = !hasItems && !!linkUrl;
    const isOpen = activeMenu === itemName;
    const gtmTitleValue = getGtmTitleValue(gtmTitle, itemName);
    const gtmInteractionTypeStr =
      itemName === 'FindDealer' ? 'cta: dealer locator' : getGtmTagValue(gtmInteractionType);
    // TODO: need flag from BE for icon config in nav item
    // for now when icon obj is not empty, display icon image
    const isNavItemIcon = !icon?.src?.includes('default_image') && icon?.src;
    const rightAlignedNavItem = navAlignment === ALIGNMENT_RIGHT;

    if (itemName?.toLowerCase?.() === FIND_DEALER_NAV_ITEM_ID.toLowerCase() && isDealerDataAvailable && !hideDealer) {
      return (
        <DealerStatusAndHours
          key={itemId}
          link={{
            value: {
              linktype: link?.linktype,
              href: linkUrl,
              target: link?.target,
            },
          }}
        />
      );
    }

    return (
      <NavItemWrapper
        key={itemId}
        as="li"
        data-testid={`header-menu-wrapper-${isLink ? 'link' : 'button'}-${itemName}`}
      >
        <NavItem
          as={!isLink && 'button'}
          field={{
            value: {
              linktype: link?.linktype,
              href: linkUrl,
              target: link?.target,
            },
          }}
          selectedMenuItem={itemName}
          ref={primaryItemsRef?.current[itemName]}
          data-testid={`header-menu-${isLink ? 'link' : 'button'}-${itemName}`}
          tabIndex={0}
          ariaLabel={label}
          onClick={() => handleEvent({ itemName, isLink, linkUrl, linkText: gtmTitleValue })}
          onKeyDown={event => handleEvent({ itemName, isLink, event, linkUrl, linkText: gtmTitleValue })}
          data-gtm-title={gtmTitleValue}
          data-gtm-interaction-type={gtmInteractionTypeStr}
          data-gtm-component-type={getGtmTagValue(gtmTags?.type)}
          data-gtm-nav-type={isLink ? 'global' : undefined}
          data-tracking-label={gtmTitleValue}
          visible={isOpen}
          {...styles.navItemPrimary}
          navItemLabel={styles.navItemLabel}
          navItemLabelUnderline={navItemLabelUnderline}
          navItemUnderline={navItemUnderline}
          titleFont={styles.navItemLabel.fontFamily}
          shouldUnderlineLinkNavItems={shouldUnderlineLinkNavItems || !rightAlignedNavItem}
        >
          <Optional when={isFindDealerIconLeftSide && !isFindDealerIconLeftSide}>
            <IconWrapper size="iconWrapper.sm">
              <NavItemIcon src={icon?.src} iconWidth={icon?.width} iconHeight={icon?.height} alt={icon?.alt} />
            </IconWrapper>
          </Optional>
          <Optional when={isNavItemIcon}>
            <NavItemIcon
              src={icon?.src}
              iconWidth={icon?.width}
              iconHeight={icon?.height}
              alt={icon?.alt}
              hasLabel={!!label}
              data-testid="header-primary-menu-item-icon"
            />
          </Optional>
          <Optional when={label}>
            <NavItemLabel
              as="span"
              shouldNavLabelHoverGrey={shouldNavLabelHoverGrey && rightAlignedNavItem}
              d="inline"
              data-testid="header-primary-menu-item-label"
              {...styles.navItemLabel}
            >
              {label}
            </NavItemLabel>
          </Optional>
          <Optional when={showNavItemIcon && !isLink && hasItems}>
            <NavItemIconWrapper ml="xs" width={['10px', '10px', '10px']}>
              <Icon name="animatedArrowUpDown" iconColor="black" toggle={isOpen} />
            </NavItemIconWrapper>
          </Optional>
        </NavItem>
        <Optional when={!isLink}>
          <Optional when={showMegaMenuSimple}>
            <MegaMenuSimple
              currentRef={megaMenuRef?.current[itemName]}
              isOpen={isOpen}
              firstOpen={firstOpen}
              itemParams={itemParams}
              gottenGtmTitle={gtmTitleValue}
            />
          </Optional>
          <Optional when={showMegaMenuGroups && hasItems}>
            <MegaMenuGroups
              currentRef={megaMenuRef?.current[itemName]}
              isOpen={isOpen}
              firstOpen={firstOpen}
              itemParams={itemParams}
              gottenGtmTitle={gtmTitleValue}
            />
          </Optional>
        </Optional>
      </NavItemWrapper>
    );
  });

  return <NavList as="ul">{renderMenuItems}</NavList>;
};

MainMenuItems.propTypes = {
  primaryItems: PropItemsType,
  gtmTags: PropTypes.shape({
    type: PropTypes.string,
  }),
};

export default MainMenuItems;
